import type { ConversaResumo, FiltroConversas } from "@ydchat/shared";
import { ROTULO_STATUS_PEDIDO, veOperacaoInteira } from "@ydchat/shared";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowUp, BellRing, Bot, Inbox, Megaphone, Moon, Search, ShoppingBag } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Avatar } from "@/components/ui/avatar";
import { Seletor } from "@/components/ui/seletor";
import { BotaoAssumir } from "./acoes-conversa";
import { STATUS_PEDIDO } from "./contexto-cliente";
import { Esqueleto } from "@/components/ui/esqueleto";
import { EstadoVazio } from "@/components/ui/estado-vazio";
import { Selo } from "@/components/ui/selo";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import {
  duracaoCurta,
  formatarHora,
  horaOuData,
  nivelEspera,
  type NivelEspera,
} from "@/lib/formatar";
import { useUsuario } from "@/lib/sessao";
import { useAgora } from "@/lib/use-agora";
import { useLimitesEspera } from "@/lib/atendimento";
import { useHorarioLoja } from "@/lib/horario";
import { useAtalho } from "@/lib/atalhos";
import { useCaixas } from "@/lib/caixas";

type Escopo = FiltroConversas["escopo"];

const COR_ESPERA: Record<NivelEspera, string> = {
  normal: "text-neutral-500",
  atencao: "text-warning-500",
  urgente: "text-danger-500",
  estourado: "bg-danger-500 text-white px-1.5 rounded-full",
};

const SITUACOES: { id: FiltroConversas["status"]; rotulo: string }[] = [
  { id: "abertas", rotulo: "Abertas" },
  { id: "retomada", rotulo: "Retomar" },
  { id: "acompanhamento", rotulo: "Acompanhar" },
  { id: "encerradas", rotulo: "Concluídas" },
];

export function ListaConversas({
  escopo,
  situacao,
  caixa,
  onEscopo,
  onSituacao,
  onCaixa,
}: {
  escopo: Escopo;
  situacao: FiltroConversas["status"];
  caixa: string | undefined;
  onEscopo: (e: Escopo) => void;
  onSituacao: (s: FiltroConversas["status"]) => void;
  onCaixa: (c: string | undefined) => void;
}) {
  const usuario = useUsuario();
  const gestor = veOperacaoInteira(usuario.papel);
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const campoBusca = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim()), 250);
    return () => clearTimeout(t);
  }, [busca]);

  // "/" foca a busca (atalho de teclado no desktop).
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      const alvo = e.target as HTMLElement;
      if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(alvo.tagName)) {
        e.preventDefault();
        campoBusca.current?.focus();
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);

  const navegarPara = useNavigate();
  const params = useParams({ strict: false }) as { conversaId?: string };
  const irPara = (id: string | undefined) =>
    id &&
    void navegarPara({
      to: "/atendimentos/$conversaId",
      params: { conversaId: id },
      search: (s) => s,
    });
  const vizinha = (passo: number) => {
    const l = consulta.data ?? [];
    if (!l.length) return;
    const i = l.findIndex((c) => c.id === params.conversaId);
    irPara(l[i < 0 ? 0 : (i + passo + l.length) % l.length]?.id);
  };
  useAtalho("proxima-conversa", () => vizinha(1));
  useAtalho("conversa-anterior", () => vizinha(-1));
  useAtalho("proxima-esperando", () => {
    const esperando = (consulta.data ?? [])
      .filter((c) => c.aguardandoRespostaDesde)
      .sort((a, b) => a.aguardandoRespostaDesde!.localeCompare(b.aguardandoRespostaDesde!));
    irPara(esperando[0]?.id);
  });

  const caixas = useCaixas();
  const variasCaixas = (caixas.data?.length ?? 0) > 1;
  // Caixa da URL que não existe mais (ou de outra loja) vira "todas".
  const caixaAtiva = caixas.data?.some((c) => c.id === caixa) ? caixa : undefined;

  const consulta = useQuery({
    queryKey: ["conversas", escopo, buscaAplicada, situacao, caixaAtiva ?? ""],
    queryFn: () => {
      const q = new URLSearchParams({ escopo, status: situacao });
      if (buscaAplicada) q.set("busca", buscaAplicada);
      if (caixaAtiva) q.set("canalId", caixaAtiva);
      return api.get<ConversaResumo[]>(`/conversas?${q}`);
    },
    refetchInterval: 60_000,
  });

  // Contagem das outras abas para a vendedora saber que há cliente sem dono.
  const filtroCaixa = caixaAtiva ? `&canalId=${caixaAtiva}` : "";
  const semResponsavel = useQuery({
    queryKey: ["conversas", "sem_responsavel", "", caixaAtiva ?? ""],
    queryFn: () =>
      api.get<ConversaResumo[]>(`/conversas?escopo=sem_responsavel&status=abertas${filtroCaixa}`),
    enabled: escopo !== "sem_responsavel",
  });

  // Clientes que escreveram com a loja fechada e ainda esperam retomada.
  const retomada = useQuery({
    queryKey: ["conversas", "retomada", escopo, caixaAtiva ?? ""],
    queryFn: () =>
      api.get<ConversaResumo[]>(`/conversas?escopo=${escopo}&status=retomada${filtroCaixa}`),
  });
  const horario = useHorarioLoja();

  const abas: { id: Escopo; rotulo: string; contagem?: number }[] = [
    { id: "minhas", rotulo: "Minhas" },
    {
      id: "sem_responsavel",
      rotulo: "Na fila",
      contagem: escopo === "sem_responsavel" ? consulta.data?.length : semResponsavel.data?.length,
    },
    ...(gestor ? [{ id: "todas" as const, rotulo: "Todas" }] : []),
  ];

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="space-y-3 border-b border-neutral-200 px-3 pb-3 pt-4">
        <div className="flex items-center justify-between px-1">
          <h1 className="flex items-center gap-2 text-xl">
            Atendimentos
            {horario.data && !horario.data.aberta && (
              <Selo tom="alerta">
                <Moon size={11} strokeWidth={2} /> Loja fechada ·{" "}
                {horario.data.abertura.toLowerCase()}
              </Selo>
            )}
          </h1>
          <button
            onClick={() => window.dispatchEvent(new Event("ydchat:abrir-busca"))}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[12.5px] font-semibold text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            title="Buscar em todas as conversas (Ctrl+K)"
          >
            <Search size={15} strokeWidth={1.75} /> Busca geral
          </button>
        </div>
        {variasCaixas && (
          <Seletor
            aria-label="Caixa de entrada"
            value={caixaAtiva ?? ""}
            onChange={(e) => onCaixa(e.target.value || undefined)}
          >
            <option value="">Todas as caixas</option>
            {caixas.data!.map((cx) => (
              <option key={cx.id} value={cx.id}>
                {cx.nome}
                {cx.telefone ? ` · ${cx.telefone}` : ""}
                {cx.principal ? " (principal)" : ""}
              </option>
            ))}
          </Seletor>
        )}
        <label className="relative block">
          <Search
            size={16}
            strokeWidth={1.75}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
          />
          <input
            ref={campoBusca}
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar nome ou telefone"
            aria-label="Buscar conversas"
            className="h-10 w-full rounded-md border border-neutral-200 bg-neutral-50 pl-9 pr-10 text-sm placeholder:text-neutral-400 focus:bg-white"
          />
          <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-neutral-200 px-1.5 text-[11px] text-neutral-500 md:block">
            /
          </kbd>
        </label>
        <div role="tablist" className="flex gap-1 rounded-lg bg-neutral-100 p-1">
          {abas.map((a) => (
            <button
              key={a.id}
              role="tab"
              aria-selected={escopo === a.id}
              onClick={() => onEscopo(a.id)}
              className={cn(
                "flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md text-[13px] font-semibold transition-colors",
                escopo === a.id
                  ? "bg-white text-neutral-900 shadow-xs"
                  : "text-neutral-500 hover:text-neutral-800",
              )}
            >
              {a.rotulo}
              {!!a.contagem && (
                <span className="min-w-5 rounded-full bg-gold-500 px-1.5 text-[11px] leading-5 text-neutral-900">
                  {a.contagem}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex gap-3 px-1 text-[12.5px]" role="radiogroup" aria-label="Situação">
          {SITUACOES.map((st) => (
            <button
              key={st.id}
              role="radio"
              aria-checked={situacao === st.id}
              onClick={() => onSituacao(st.id)}
              className={cn(
                "border-b-2 pb-0.5 font-semibold transition-colors",
                situacao === st.id
                  ? "border-gold-500 text-neutral-900"
                  : "border-transparent text-neutral-500 hover:text-neutral-800",
              )}
            >
              {st.rotulo}
              {st.id === "retomada" && !!retomada.data?.length && situacao !== "retomada" && (
                <span className="ml-1 rounded-full bg-gold-500 px-1.5 text-[11px] text-neutral-900">
                  {retomada.data.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="rolagem-fina flex-1 overflow-y-auto pb-16 md:pb-0">
        {consulta.isPending ? (
          <ListaEsqueleto />
        ) : consulta.isError ? (
          <EstadoVazio
            icone={Inbox}
            titulo="Não foi possível carregar"
            descricao={consulta.error.message}
          />
        ) : consulta.data.length === 0 ? (
          <EstadoVazio
            icone={Inbox}
            titulo={
              buscaAplicada
                ? "Nada encontrado"
                : situacao === "retomada"
                  ? "Nada para retomar"
                  : situacao === "acompanhamento"
                    ? "Nenhum lembrete marcado"
                    : situacao === "encerradas"
                      ? "Nenhum atendimento concluído"
                      : escopo === "minhas"
                        ? "Nenhum atendimento seu agora"
                        : "Fila vazia"
            }
            descricao={
              buscaAplicada
                ? "Tente outro nome ou telefone."
                : situacao === "retomada"
                  ? "Aqui ficam as clientes que escreveram com a loja fechada, até alguém responder."
                  : situacao === "acompanhamento"
                    ? "Use “Acompanhar” no menu da conversa para receber um lembrete."
                    : situacao !== "abertas"
                      ? undefined
                      : escopo === "minhas"
                        ? "Quando um cliente for direcionado a você, ele aparece aqui."
                        : undefined
            }
          />
        ) : (
          <ul>
            {consulta.data.map((c) => (
              <ItemConversa
                key={c.id}
                conversa={c}
                mostrarResponsavel={escopo === "todas"}
                mostrarCaixa={variasCaixas && !caixaAtiva}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ItemConversa({
  conversa: c,
  mostrarResponsavel,
  mostrarCaixa,
}: {
  conversa: ConversaResumo;
  mostrarResponsavel: boolean;
  mostrarCaixa: boolean;
}) {
  const params = useParams({ strict: false }) as { conversaId?: string };
  const agora = useAgora(30_000);
  const limites = useLimitesEspera();
  const ativa = params.conversaId === c.id;
  const espera = c.aguardandoRespostaDesde
    ? nivelEspera(c.aguardandoRespostaDesde, limites, agora)
    : null;

  return (
    <li className="relative">
      <Link
        to="/atendimentos/$conversaId"
        params={{ conversaId: c.id }}
        search={(s) => s}
        className={cn(
          "flex gap-3 border-b border-neutral-100 px-4 py-3 transition-colors",
          ativa ? "bg-gold-50" : "hover:bg-neutral-50",
        )}
      >
        <div className="relative">
          <Avatar nome={c.contato.nome} />
          {c.prioridade > 0 && (
            <span
              title="Prioridade"
              className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-gold-500 text-neutral-900 ring-2 ring-white"
            >
              <ArrowUp size={10} strokeWidth={2.5} />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p
              className={cn(
                "truncate text-[15px]",
                c.naoLidas ? "font-bold text-neutral-900" : "font-semibold text-neutral-800",
              )}
            >
              {c.contato.nome}
            </p>
            {c.ultimaMensagem && (
              <span className="shrink-0 text-xs text-neutral-500">
                {horaOuData(c.ultimaMensagem.em)}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <p
              className={cn(
                "truncate text-[13px]",
                c.naoLidas ? "text-neutral-800" : "text-neutral-500",
              )}
            >
              {c.ultimaMensagem?.direcao === "saida" && (
                <span className="text-neutral-500">Você: </span>
              )}
              {c.ultimaMensagem?.texto ?? "Sem mensagens"}
            </p>
            {c.naoLidas > 0 && (
              <span className="min-w-5 shrink-0 rounded-full bg-gold-500 px-1.5 text-center text-[11px] font-bold leading-5 text-neutral-900">
                {c.naoLidas}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {c.canal.comunicacao && (
              <Selo>
                <Megaphone size={10} strokeWidth={2} /> Canal automático
              </Selo>
            )}
            {mostrarCaixa && !c.canal.comunicacao && (
              <Selo>
                <Inbox size={10} strokeWidth={2} /> {c.canal.nome}
              </Selo>
            )}
            {espera && c.aguardandoRespostaDesde && (
              <span
                className={cn("text-[11px] font-bold", COR_ESPERA[espera])}
                title="Tempo esperando resposta"
              >
                Esperando {duracaoCurta(c.aguardandoRespostaDesde, agora)}
              </span>
            )}
            {c.acompanharEm && (
              <Selo tom={new Date(c.acompanharEm).getTime() <= agora ? "alerta" : "dourado"}>
                <BellRing size={10} strokeWidth={2} /> {horaOuData(c.acompanharEm)}{" "}
                {formatarHora(c.acompanharEm)}
              </Selo>
            )}
            {c.pedido && (
              <Selo tom={STATUS_PEDIDO[c.pedido.status].tom}>
                #{c.pedido.numero} · {ROTULO_STATUS_PEDIDO[c.pedido.status]}
              </Selo>
            )}
            {c.modo === "fora_horario" && (
              <Selo tom="dourado">
                <Moon size={10} strokeWidth={2} /> Loja fechada
              </Selo>
            )}
            {c.retomarDesde && c.modo !== "fora_horario" && <Selo tom="alerta">Retomar</Selo>}
            {c.modo === "ponte" && (
              <Selo tom="dourado">
                <Bot size={10} strokeWidth={2} /> IA conduzindo
              </Selo>
            )}
            {c.temCarrinho && (
              <Selo>
                <ShoppingBag size={10} strokeWidth={2} /> Carrinho
              </Selo>
            )}
            {c.status === "encerrada" && c.encerradaEm && (
              <Selo>Concluída {horaOuData(c.encerradaEm)}</Selo>
            )}
            {!c.contato.erpClienteId && <Selo tom="dourado">Novo cliente</Selo>}
            {c.etiquetas.slice(0, 2).map((e) => (
              <Selo key={e.id} cor={e.cor}>
                {e.nome}
              </Selo>
            ))}
            {mostrarResponsavel && (
              <span className="ml-auto truncate text-[11px] text-neutral-500">
                {c.responsavel?.nome ?? "Sem responsável"}
              </span>
            )}
            {/* espaço para o botão Assumir, que fica fora do link */}
            {!c.responsavel && !mostrarResponsavel && !c.canal.comunicacao && (
              <span className="ml-auto h-7 w-20" />
            )}
          </div>
        </div>
      </Link>
      {!c.responsavel && !mostrarResponsavel && !c.canal.comunicacao && (
        <div className="absolute bottom-3 right-4">
          <BotaoAssumir conversa={c} compacto />
        </div>
      )}
    </li>
  );
}

function ListaEsqueleto() {
  return (
    <div className="space-y-px">
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} className="flex gap-3 px-4 py-3">
          <Esqueleto className="size-10 rounded-full" />
          <div className="flex-1 space-y-2 py-1">
            <Esqueleto className="h-3.5 w-2/3" />
            <Esqueleto className="h-3 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
